import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Orb } from "../components/Orb";
import { Loader2 } from "lucide-react";

export default function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else await register(email.trim(), password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stealth-bg min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 rounded-3xl overflow-hidden glass-panel">
        {/* Left: brand + orb */}
        <div className="relative hidden lg:flex flex-col justify-between p-10" style={{ background: "linear-gradient(160deg, rgba(235,235,235,0.08), rgba(235,235,235,0.03))" }}>
          <div>
            <div className="label-mono">Stealth Assistance</div>
            <h1 className="font-heading text-4xl font-medium mt-3 leading-tight">Your AI,<br />Connected to Your World.</h1>
            <p className="text-slate-400 mt-4 text-sm max-w-xs">Talk to Anna or Sam. Natural voice, real conversation — not another chatbot.</p>
          </div>
          <div className="flex justify-center py-6">
            <Orb state="idle" size={230} />
          </div>
          <div className="text-xs text-slate-500">Phase 1 · Conversation & Voice</div>
        </div>

        {/* Right: form */}
        <div className="p-8 sm:p-10 flex flex-col justify-center">
          <div className="lg:hidden flex justify-center mb-6"><Orb state="idle" size={150} /></div>
          <h2 className="font-heading text-2xl font-medium">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-slate-400 text-sm mt-1">{mode === "login" ? "Sign in to continue your conversation." : "A few seconds and you're in."}</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="label-mono">Email</label>
              <input
                data-testid="auth-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-sm outline-none focus:border-mono-400/60 transition-colors"
                placeholder="you@domain.com"
              />
            </div>
            <div>
              <label className="label-mono">Password</label>
              <input
                data-testid="auth-password-input"
                type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-sm outline-none focus:border-mono-400/60 transition-colors"
                placeholder="At least 6 characters"
              />
            </div>
            {error && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}
            <button
              data-testid="auth-submit-button"
              type="submit" disabled={loading}
              className="w-full rounded-full py-3 font-medium text-[#000000] flex items-center justify-center gap-2 transition-all disabled:opacity-60"
              style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)", boxShadow: "0 0 30px rgba(235,235,235,0.25)" }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-slate-400 hover:text-mono-300 transition-colors self-start"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
